import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { getModifierKey } from 'utils/ui';

const STORAGE_KEY = 'hasSeenGridShortcuts';

function MouseLeftClickIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path
        d='M6 10V17C6 19.7614 8.6863 22 12 22C15.3137 22 18 19.7614 18 17V10'
        strokeLinecap='round'
      />
      <path
        d='M6 10V7C6 4.2386 8.6863 2 12 2C15.3137 2 18 4.2386 18 7V10'
        strokeLinecap='round'
      />
      <path d='M12 2V10M6 10H18' strokeLinecap='round' />
      <path
        d='M6 7C6 4.2386 8.6863 2 12 2V10H6V7Z'
        fill='currentColor'
        stroke='none'
      />
    </svg>
  );
}

function MouseRightClickIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path
        d='M6 10V17C6 19.7614 8.6863 22 12 22C15.3137 22 18 19.7614 18 17V10'
        strokeLinecap='round'
      />
      <path
        d='M6 10V7C6 4.2386 8.6863 2 12 2C15.3137 2 18 4.2386 18 7V10'
        strokeLinecap='round'
      />
      <path d='M12 2V10M6 10H18' strokeLinecap='round' />
      <path
        d='M12 2C15.3137 2 18 4.2386 18 7V10H12V2Z'
        fill='currentColor'
        stroke='none'
      />
    </svg>
  );
}

function MouseScrollIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path
        d='M6 10V17C6 19.7614 8.6863 22 12 22C15.3137 22 18 19.7614 18 17V10'
        strokeLinecap='round'
      />
      <path
        d='M6 10V7C6 4.2386 8.6863 2 12 2C15.3137 2 18 4.2386 18 7V10'
        strokeLinecap='round'
      />
      <path d='M12 2V10M6 10H18' strokeLinecap='round' />
      <rect x='10.5' y='4' width='3' height='5' rx='1.5' fill='currentColor' />
    </svg>
  );
}

type ShortcutItemProps = {
  keys: string[];
  description: ReactNode;
  type?: 'keyboard' | 'left click' | 'right click' | 'scroll';
};

function ShortcutItem({
  keys,
  description,
  type = 'keyboard'
}: ShortcutItemProps) {
  const renderIcon = () => {
    if (type === 'left click') {
      return <MouseLeftClickIcon className='h-6 w-6' />;
    }
    if (type === 'right click') {
      return <MouseRightClickIcon className='h-6 w-6' />;
    }
    if (type === 'scroll') {
      return <MouseScrollIcon className='h-6 w-6' />;
    }
    return null;
  };

  return (
    <div className='flex items-center gap-3'>
      <div className='flex shrink-0 items-center gap-1'>
        {keys.map((key) =>
          type === 'keyboard' ? (
            <kbd
              key={key}
              className='inline-flex min-w-[2rem] items-center justify-center rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 shadow-sm'
            >
              {key}
            </kbd>
          ) : (
            <span
              key={key}
              className='inline-flex items-center gap-0.5 px-1 py-1 text-xs font-medium text-gray-600'
            >
              {renderIcon()}
              {key || null}
            </span>
          )
        )}
      </div>
      <span className='text-sm text-gray-600'>{description}</span>
    </div>
  );
}

type KeyboardMouseShortcutsPopupProps = Readonly<{
  showSelectSpaces?: boolean;
  showAdvancedProperties?: boolean;
  showLayoutImage?: boolean;
}>;

export default function KeyboardMouseShortcutsPopup({
  showSelectSpaces = true,
  showAdvancedProperties = true,
  showLayoutImage = true
}: KeyboardMouseShortcutsPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const modKey = useMemo(() => getModifierKey(), []);

  useEffect(() => {
    const hasSeen = localStorage.getItem(STORAGE_KEY);
    if (!hasSeen) {
      setIsOpen(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  if (!isOpen) {
    return (
      <button
        type='button'
        onClick={handleOpen}
        className='absolute bottom-2 left-2 z-50 rounded-full border border-gray-200 bg-white p-2 text-gray-500 shadow-lg hover:bg-gray-50 hover:text-gray-700'
        aria-label='Show keyboard shortcuts'
      >
        <QuestionMarkCircleIcon className='h-5 w-5' />
      </button>
    );
  }

  return (
    <div className='absolute bottom-2 left-2 z-50 animate-fade-in rounded-lg border border-gray-200 bg-white p-4 shadow-lg'>
      <div className='mb-3 flex items-center justify-between'>
        <h3 className='text-sm font-semibold text-gray-900'>
          Keyboard / Mouse Shortcuts
        </h3>
        <button
          type='button'
          onClick={handleDismiss}
          className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          aria-label='Dismiss shortcuts popup'
        >
          <XMarkIcon className='h-4 w-4' />
        </button>
      </div>
      <div className='flex gap-8'>
        <div className='flex flex-col gap-2.5'>
          <ShortcutItem
            keys={['Space']}
            description='Hold and click + drag to pan'
          />
          {showSelectSpaces && (
            <>
              <ShortcutItem
                keys={['Shift']}
                description='Hold and click to multi-select'
              />
              <ShortcutItem
                keys={[modKey, 'C']}
                description='Copy selected space(s)'
              />
              <ShortcutItem
                keys={[modKey, 'V']}
                description='Paste copied space(s)'
              />
              <ShortcutItem keys={[modKey, 'Z']} description='Undo' />
              <ShortcutItem keys={[modKey, 'Shift', 'Z']} description='Redo' />
            </>
          )}
        </div>
        <div className='flex flex-col gap-2.5'>
          <ShortcutItem
            keys={['']}
            description='Zoom in / zoom out'
            type='scroll'
          />
          <ShortcutItem keys={['']} description='Pan view' type='right click' />
          {showAdvancedProperties && (
            <ShortcutItem
              keys={['x 2']}
              description='Open space properties'
              type='left click'
            />
          )}
          {showLayoutImage && (
            <ShortcutItem
              keys={['x 2']}
              description='Select layout image'
              type='left click'
            />
          )}
        </div>
      </div>
    </div>
  );
}
